import { AlertTriangle, Check, FileText, FolderOpen, Import, Trash2, X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useRef, useState } from "react";

import { normalizeFolderPath } from "@domain/notebook-folders";
import type { ImportNotesResult } from "@/hooks/useNotebookFolders";

interface ImportMarkdownModalProps {
  open: boolean;
  onClose: () => void;
  /** All existing folder paths (sorted) for the destination select. */
  folders: string[];
  /** Persists the reviewed files into `folder` ("" = root) via the notebook
   *  action surface. Resolves with per-file results; null means the batch
   *  didn't start (invalid folder name — the owner surfaces that error). */
  onImport: (
    files: Array<{ title: string; content: string }>,
    folder: string,
  ) => Promise<ImportNotesResult | null>;
  /** Pre-select a destination folder (e.g. opened from a folder's menu). */
  initialFolder?: string;
}

interface PendingFile {
  name: string;
  title: string;
  content: string;
  size: number;
}

/** Notion exports name files "Page Title 0a1b…32hex.md" — drop the hash so
 *  imported notes get their human title back. */
function titleFromFilename(filename: string): string {
  return filename
    .replace(/\.(md|markdown)$/i, "")
    .replace(/\s+[0-9a-f]{32}$/i, "")
    .trim();
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

const NEW_FOLDER = "__new__";

/**
 * Import markdown files as notes — from Notion, Obsidian, or anywhere else
 * that exports .md. One-way and lossless: notes land as plain markdown files
 * in the vault, so the agent reads them immediately and nothing gets locked
 * in. Files are reviewed (and can be pruned) before the import runs.
 */
export function ImportMarkdownModal({
  open,
  onClose,
  folders,
  onImport,
  initialFolder = "",
}: ImportMarkdownModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [pending, setPending] = useState<PendingFile[]>([]);
  const [destination, setDestination] = useState(initialFolder);
  const [newFolderName, setNewFolderName] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportNotesResult | null>(null);

  async function addFiles(fileList: FileList | null) {
    if (!fileList) return;
    const files = Array.from(fileList).filter((f) => /\.(md|markdown)$/i.test(f.name));
    const loaded = await Promise.all(
      files.map(async (file) => ({
        name: file.name,
        title: titleFromFilename(file.name),
        content: await file.text(),
        size: file.size,
      })),
    );
    setPending((prev) => {
      const seen = new Set(prev.map((p) => p.name));
      return [...prev, ...loaded.filter((f) => !seen.has(f.name))];
    });
  }

  function resolveDestination(): string {
    if (destination === NEW_FOLDER) return normalizeFolderPath(newFolderName);
    return destination;
  }

  async function handleImport() {
    setBusy(true);
    try {
      const outcome = await onImport(
        pending.map(({ title, content }) => ({ title, content })),
        resolveDestination(),
      );
      // null = batch never started (bad folder name) — keep the review list so
      // the user can fix the destination; the owner shows the error banner.
      if (outcome !== null) {
        setResult(outcome);
        setPending([]);
      }
    } finally {
      setBusy(false);
    }
  }

  function handleClose() {
    if (busy) return;
    setPending([]);
    setDestination(initialFolder);
    setNewFolderName("");
    setResult(null);
    setDragOver(false);
    onClose();
  }

  const destinationLabel = resolveDestination() || "Notebook root";

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ type: "spring", stiffness: 420, damping: 32 }}
            className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl border border-black/10 bg-white shadow-2xl dark:border-white/10 dark:bg-surface-dark"
          >
            <div className="flex items-center justify-between border-b border-black/5 px-5 py-4 dark:border-white/10">
              <div className="flex items-center gap-2">
                <Import className="h-5 w-5 text-brand-coral-dark dark:text-brand-coral" strokeWidth={2.2} />
                <h2 className="font-heading text-lg font-bold text-black dark:text-white">
                  Import markdown
                </h2>
              </div>
              <button
                onClick={handleClose}
                aria-label="Close"
                className="flex h-8 w-8 items-center justify-center rounded-full text-black/40 transition-colors hover:bg-black/5 hover:text-black/70 dark:text-white/40 dark:hover:bg-white/10 dark:hover:text-white/70"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-5 py-5">
              {result !== null ? (
                <div className="flex flex-col items-center py-4 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-green/15">
                    <Check className="h-6 w-6 text-accent-green" strokeWidth={2.4} />
                  </span>
                  <p className="mt-3 text-sm font-bold text-black dark:text-white">
                    Imported {result.imported} {result.imported === 1 ? "note" : "notes"} into{" "}
                    {destinationLabel}.
                  </p>
                  <p className="mt-1 text-xs text-black/50 dark:text-white/50">
                    Your agent can read them from here on.
                  </p>
                  {result.failed.length > 0 && (
                    <div className="mt-3 w-full rounded-xl border border-amber-300/60 bg-amber-50 px-3 py-2 text-left dark:border-amber-500/30 dark:bg-amber-950/30">
                      <p className="flex items-center gap-1.5 text-xs font-bold text-amber-800 dark:text-amber-300">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.4} />
                        {result.failed.length} {result.failed.length === 1 ? "file" : "files"} didn&apos;t import
                      </p>
                      <p className="mt-1 break-words text-[11px] leading-relaxed text-amber-800/80 dark:text-amber-300/80">
                        {result.failed.join(", ")}
                      </p>
                    </div>
                  )}
                  <div className="mt-4 flex items-center gap-2">
                    <button
                      onClick={() => setResult(null)}
                      className="rounded-full border border-black/15 px-5 py-2 text-sm font-bold text-black/70 transition-colors hover:bg-black/5 dark:border-white/20 dark:text-white/70 dark:hover:bg-white/5"
                    >
                      Import more
                    </button>
                    <button
                      onClick={handleClose}
                      className="rounded-full bg-home-yellow px-6 py-2 text-sm font-bold text-home-ink transition-transform active:scale-95"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm leading-relaxed text-black/60 dark:text-white/60">
                    Bring notes from Notion, Obsidian, or anywhere that exports markdown.
                    Formatting comes across untouched — and stays plain markdown, so nothing
                    gets locked in.
                  </p>

                  <input
                    ref={inputRef}
                    type="file"
                    accept=".md,.markdown,text/markdown"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      void addFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                  <button
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                      void addFiles(e.dataTransfer.files);
                    }}
                    className={`mt-4 flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed py-6 transition-colors ${
                      dragOver
                        ? "border-brand-coral bg-brand-coral/10 text-brand-coral-dark dark:text-brand-coral"
                        : "border-black/20 text-black/60 hover:border-brand-coral/50 hover:bg-brand-coral/[0.03] hover:text-brand-coral-dark dark:border-white/20 dark:text-white/60 dark:hover:border-brand-coral/60 dark:hover:text-brand-coral"
                    }`}
                  >
                    <FileText className="h-6 w-6" strokeWidth={1.8} />
                    <span className="text-sm font-bold">
                      {dragOver ? "Drop them in" : "Drop markdown files or click to browse"}
                    </span>
                    <span className="text-xs text-black/40 dark:text-white/40">
                      .md and .markdown files
                    </span>
                  </button>

                  {pending.length > 0 && (
                    <>
                      <ul className="mt-4 max-h-40 overflow-y-auto rounded-xl border border-black/8 dark:border-white/10">
                        {pending.map((file) => (
                          <li
                            key={file.name}
                            className="flex items-center gap-2.5 border-b border-black/5 px-3 py-2 last:border-b-0 dark:border-white/5"
                          >
                            <FileText className="h-4 w-4 shrink-0 text-black/40 dark:text-white/40" strokeWidth={2} />
                            <span className="min-w-0 flex-1 truncate text-[13px] font-bold text-black dark:text-white">
                              {file.title || "Untitled"}
                            </span>
                            <span className="shrink-0 text-[11px] text-black/40 dark:text-white/40">
                              {formatSize(file.size)}
                            </span>
                            <button
                              onClick={() =>
                                setPending((prev) => prev.filter((p) => p.name !== file.name))
                              }
                              aria-label={`Remove ${file.title}`}
                              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-black/35 transition-colors hover:bg-black/5 hover:text-red-600 dark:text-white/35 dark:hover:bg-white/10"
                            >
                              <Trash2 className="h-3.5 w-3.5" strokeWidth={2.2} />
                            </button>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-4 flex items-center gap-2">
                        <FolderOpen className="h-4 w-4 shrink-0 text-black/45 dark:text-white/45" strokeWidth={2.2} />
                        <label htmlFor="import-destination" className="text-xs font-bold text-black/55 dark:text-white/55">
                          Into
                        </label>
                        <select
                          id="import-destination"
                          value={destination}
                          onChange={(e) => setDestination(e.target.value)}
                          className="min-w-0 flex-1 rounded-lg border border-black/10 bg-transparent px-2 py-1.5 text-[13px] font-bold text-black outline-none dark:border-white/15 dark:bg-surface-dark dark:text-white"
                        >
                          <option value="">Notebook root</option>
                          {folders.map((f) => (
                            <option key={f} value={f}>
                              {f.split("/").join(" / ")}
                            </option>
                          ))}
                          <option value={NEW_FOLDER}>New folder…</option>
                        </select>
                      </div>
                      {destination === NEW_FOLDER && (
                        <input
                          autoFocus
                          value={newFolderName}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          placeholder="Folder name"
                          className="mt-2 w-full rounded-lg border border-brand-coral/40 bg-transparent px-3 py-1.5 text-[13px] text-black outline-none placeholder:text-black/35 dark:text-white dark:placeholder:text-white/35"
                        />
                      )}

                      <button
                        onClick={() => void handleImport()}
                        disabled={busy || (destination === NEW_FOLDER && !newFolderName.trim())}
                        className="mt-4 w-full rounded-full bg-home-yellow py-2.5 text-sm font-bold text-home-ink transition-transform active:scale-95 disabled:opacity-40"
                      >
                        {busy
                          ? "Importing…"
                          : `Import ${pending.length} ${pending.length === 1 ? "note" : "notes"}`}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
